import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "./api";
import { Auth, type Session } from "./auth";
import { ClusterPage } from "./ClusterPage";
import { Board, EmptyBoard, Header, Hero } from "./Dashboard";
import { DeploysView } from "./Deploys";
import { Detail } from "./Detail";
import { ErrorsView } from "./Errors";
import { duration } from "./format";
import {
  hasInfra,
  STALE_AFTER_MS,
  useDetailTarget,
  useHubData,
  useInventory,
  useMaintenance,
  useNodeUsage,
  useNow,
  usePref,
  useRecentDeploys,
  useView,
  VIEWS,
} from "./hooks";
import { buildViewModel, CLUSTER_TARGET, type Density, readReportHash } from "./model";
import { Palette } from "./Palette";
import { ReportPage } from "./Report";
import { Forbidden, Loading, SignIn, Unreachable } from "./Screens";
import { SecurityView } from "./Security";
import { ServersView } from "./Servers";
import { UsersView } from "./Users";

type Phase =
  | { kind: "boot" }
  | { kind: "unreachable"; error: string }
  | { kind: "signedOut"; expired: boolean; error: string | null }
  | { kind: "forbidden"; details: Record<string, unknown>; session: Session }
  | { kind: "signedIn"; session: Session };

const BOOT_RETRY_MS = 10_000;
const IDLE_CURSOR_MS = 3_000;
/** Nudges the whole panel a pixel or two every minute in kiosk mode (screen burn-in). */
const BURN_IN = [
  "none",
  "translate(2px, 1px)",
  "translate(-1px, 2px)",
  "translate(-2px, -1px)",
  "translate(1px, -2px)",
];

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: "boot" });
  const [auth, setAuth] = useState<Auth | null>(null);
  const [theme, setTheme] = usePref("theme", "dark", ["dark", "light"] as const);
  const [density, setDensity] = usePref<Density>("density", "auto", [
    "auto",
    "comfortable",
    "compact",
  ]);
  const [kiosk, setKiosk] = useState(false);
  const [lastMove, setLastMove] = useState(() => Date.now());
  const now = useNow();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const boot = async () => {
      try {
        const instance = new Auth(await api.authConfig());
        setAuth(instance);
        try {
          const session = await instance.restore();
          setPhase(
            session
              ? { kind: "signedIn", session }
              : { kind: "signedOut", expired: false, error: null },
          );
        } catch (error) {
          setPhase({ kind: "signedOut", expired: false, error: (error as Error).message });
        }
      } catch (error) {
        setPhase({
          kind: "unreachable",
          error:
            error instanceof ApiError
              ? `the hub answered ${error.message}`
              : "no answer from the hub",
        });
        timer = setTimeout(boot, BOOT_RETRY_MS);
      }
    };
    boot();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onMove = () => setLastMove(Date.now());
    const onFullscreen = () => {
      if (!document.fullscreenElement) setKiosk(false);
    };
    window.addEventListener("mousemove", onMove);
    document.addEventListener("fullscreenchange", onFullscreen);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("fullscreenchange", onFullscreen);
    };
  }, []);

  const toggleKiosk = () => {
    const on = !kiosk;
    setKiosk(on);
    setLastMove(Date.now());
    if (on) document.documentElement.requestFullscreen?.().catch(() => {});
    else if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  };

  const signOut = useCallback(async (session?: Session) => {
    await session?.signOut();
    setPhase({ kind: "signedOut", expired: false, error: null });
  }, []);

  let content: ReactNode;
  switch (phase.kind) {
    case "boot":
      content = <Loading />;
      break;
    case "unreachable":
      content = <Unreachable error={phase.error} />;
      break;
    case "signedOut":
      content = (
        <SignIn
          oidcEnabled={auth?.oidcEnabled ?? false}
          expired={phase.expired}
          error={phase.error}
          onOidc={() => auth?.signIn()}
          onToken={(token) =>
            auth && setPhase({ kind: "signedIn", session: auth.signInWithToken(token) })
          }
        />
      );
      break;
    case "forbidden":
      content = <Forbidden details={phase.details} onSignOut={() => signOut(phase.session)} />;
      break;
    case "signedIn":
      content = (
        <Live
          session={phase.session}
          now={now}
          theme={theme}
          density={density}
          kiosk={kiosk}
          onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
          onToggleDensity={(compact) => setDensity(compact ? "comfortable" : "compact")}
          onToggleKiosk={toggleKiosk}
          onSignOut={() => signOut(phase.session)}
          onExpired={() => setPhase({ kind: "signedOut", expired: true, error: null })}
          onForbidden={(details) =>
            setPhase({ kind: "forbidden", details, session: phase.session })
          }
        />
      );
      break;
  }

  const idle = kiosk && now - lastMove > IDLE_CURSOR_MS;
  return (
    <div
      className="ch"
      data-theme={theme}
      data-kiosk={kiosk ? "on" : "off"}
      data-idle={idle}
      style={{ transform: kiosk ? BURN_IN[Math.floor(now / 60_000) % BURN_IN.length] : "none" }}
    >
      {content}
    </div>
  );
}

interface LiveProps {
  session: Session;
  now: number;
  theme: "dark" | "light";
  density: Density;
  kiosk: boolean;
  onToggleTheme(): void;
  onToggleDensity(compact: boolean): void;
  onToggleKiosk(): void;
  onSignOut(): void;
  onExpired(): void;
  onForbidden(details: Record<string, unknown>): void;
}

/** The signed-in panel: polls the hub and renders the dashboard. */
function Live(props: LiveProps) {
  const { session, now, density, kiosk } = props;
  const data = useHubData(session, { onExpired: props.onExpired, onForbidden: props.onForbidden });
  const view = useView();
  const [detailTarget, closeDetail] = useDetailTarget(VIEWS.find((v) => v.id === view)?.href ?? "");
  const watchesCluster = data.status?.checks.some((c) => c.target === CLUSTER_TARGET) ?? false;
  const usage = useNodeUsage(session, watchesCluster);
  const hasDeploys = data.status?.checks.some((c) => c.data?.deployUrl) ?? false;
  const deploys = useRecentDeploys(session, hasDeploys);
  const maintenance = useMaintenance(session);
  const inventory = useInventory(session);
  const [searching, setSearching] = useState(false);
  // Ctrl/⌘ K anywhere, or "/" when not typing in a field, opens the quick search.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const field = (event.target as HTMLElement | null)?.closest?.(
        "input, textarea, select, [contenteditable]",
      );
      const combo = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      if (combo || (event.key === "/" && !field && !event.ctrlKey && !event.metaKey)) {
        event.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const menuViews = VIEWS.filter(
    (v) => (!v.admin || data.me?.role === "admin") && (!v.infra || hasInfra(data.me)),
  );

  const vm = useMemo(
    () =>
      data.status
        ? buildViewModel({
            checks: data.status.checks,
            stats: data.stats,
            open: data.open,
            resolved: data.resolved,
            now,
            density,
            ...(usage && { usage }),
            ...(deploys && { deploys }),
            ...(maintenance.data && { quiet: maintenance.data.active }),
            ...(inventory && { inventory }),
          })
        : null,
    [data, now, density, usage, deploys, maintenance.data, inventory],
  );

  const detailCard = detailTarget ? vm?.cards.find((c) => c.name === detailTarget) : undefined;

  const stale =
    data.lastError !== null &&
    (data.lastSuccess === null || now - data.lastSuccess > STALE_AFTER_MS);
  const staleSince = data.lastSuccess;

  const updatedText = stale
    ? staleSince
      ? `No update for ${duration(now - staleSince)}`
      : "No data yet"
    : data.lastSuccess
      ? `Updated ${Math.max(0, Math.floor((now - data.lastSuccess) / 1000))} s ago`
      : "Connecting…";

  const testNotification =
    data.me?.role === "admin"
      ? async () => {
          const token = await session.getToken();
          if (!token) return "Session expired.";
          try {
            const result = await api.testNotification(token);
            return result.results.every((r) => r.ok)
              ? "Sent — check your phone."
              : "Some notifiers failed.";
          } catch (error) {
            return error instanceof ApiError && error.status === 409
              ? "No notifiers are configured."
              : "Sending failed.";
          }
        }
      : undefined;

  return (
    <div className="live" data-density={vm?.compact ? "compact" : "comfortable"}>
      <Header
        vm={vm}
        now={now}
        stale={stale}
        updatedText={updatedText}
        theme={props.theme}
        compact={vm?.compact ?? false}
        kiosk={kiosk}
        view={view}
        user={data.me ? { name: data.me.name, role: data.me.role, infra: hasInfra(data.me) } : null}
        onToggleTheme={props.onToggleTheme}
        onToggleDensity={() => props.onToggleDensity(vm?.compact ?? false)}
        onToggleKiosk={props.onToggleKiosk}
        onSignOut={props.onSignOut}
        onSearch={() => setSearching(true)}
        {...(testNotification && { onTestNotification: testNotification })}
      />
      {!vm && !stale && <Loading />}
      {(vm || stale) && (view === "status" || stale) && (
        <Hero
          hero={vm?.hero ?? { tone: "unknown", title: "", detail: "" }}
          now={now}
          stale={stale ? { since: staleSince, reason: data.lastError ?? "unknown error" } : null}
        />
      )}
      {vm &&
        view === "status" &&
        (vm.empty ? <EmptyBoard /> : <Board vm={vm} now={now} stale={stale} />)}
      {vm && view === "deploys" && (
        <DeploysView
          board={vm.deployBoard}
          now={now}
          stale={stale}
          deploySites={data.me ? data.me.deploySites : []}
        />
      )}
      {vm && view === "errors" && (
        <ErrorsView
          board={vm.errorBoard}
          apps={vm.appErrors}
          session={session}
          now={now}
          stale={stale}
        />
      )}
      {vm && view === "security" && hasInfra(data.me) && (
        <SecurityView board={vm.securityBoard} stale={stale} />
      )}
      {vm && view === "cluster" && data.status && hasInfra(data.me) && (
        <ClusterPage
          vm={vm}
          checks={data.status.checks}
          session={session}
          now={now}
          stale={stale}
        />
      )}
      {vm && view === "servers" && hasInfra(data.me) && (
        <ServersView board={vm.serverBoard} session={session} now={now} stale={stale} />
      )}
      {view === "users" && data.me?.role === "admin" && <UsersView session={session} now={now} />}
      {view === "report" &&
        (() => {
          const at = readReportHash(window.location.hash);
          return at ? (
            <ReportPage session={session} target={at.target} month={at.month} now={now} />
          ) : null;
        })()}
      {searching && vm && !kiosk && (
        <Palette cards={vm.cards} views={menuViews} onClose={() => setSearching(false)} />
      )}
      {detailCard && !kiosk && (
        <Detail
          key={detailCard.name}
          card={detailCard}
          session={session}
          now={now}
          canDeploy={
            data.me
              ? data.me.deploySites === null || data.me.deploySites.includes(detailCard.name)
              : false
          }
          maintenance={maintenance.data}
          isAdmin={data.me?.role === "admin"}
          onMaintenanceChange={maintenance.reload}
          onClose={closeDetail}
        />
      )}
    </div>
  );
}
